import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Search, Plus, X, Check, Info, ChevronDown, Sparkles } from 'lucide-react';
import { ExtractionStatus } from './ExtractionStatus';

/**
 * Cost Ledger L-1 §6.1 — cost fields panel inside the Capture confirm
 * screen. Category is the mode switch: no supplier / value / reference /
 * tags fields render until a category is chosen.
 *
 * Ordering top-to-bottom: Category → Supplier → Value → Reference → Tags.
 * Value before reference because it's the field most likely to be filled
 * on a receipt.
 *
 * Emits the shape:
 *   { category, supplier_id, supplier_name, value, reference, tags: [] }
 * via `onChange`. Parent sends via FormData on capture submit.
 */

const CATEGORIES = [
  { key: 'material', label: 'Material' },
  { key: 'plant',    label: 'Plant' },
  { key: 'other',    label: 'Other' },
];

const CLEARED_FIELDS = { supplier_id: null, supplier_name: null, value: '', reference: '', tags: [], invoice_date: '' };

function FromReceipt() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-300 ml-1.5" data-testid="capture-from-receipt-hint">
      <Sparkles className="w-3 h-3" /> from receipt
    </span>
  );
}

/**
 * mode='full'     — Material / Plant / Other chips (general Capture).
 * mode='material' — launched from the Site Diary: Material is on by default
 *                   with a single "Photo only" opt-out; no other categories.
 * extraction      — AI read result {status, fields, supplier_match, applied}
 *                   used only for hints; the values live in `value`.
 */
export function CostFieldsPanel({ value, onChange, mode = 'full', extraction = null }) {
  const patch = (partial) => onChange({ ...value, ...partial });
  const category = value.category || null;
  const materialMode = mode === 'material';
  const photoOnly = materialMode && !category;
  const fromReceipt = (k) => (
    extraction?.status === 'done' && extraction.applied?.[k] != null &&
    String(value[k] ?? '') !== '' && String(value[k] ?? '') === String(extraction.applied[k])
  );

  return (
    <div className="space-y-4" data-testid="cost-fields-panel">
      {materialMode ? (
        <div
          className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 border ${photoOnly ? 'bg-[#2A3240] border-white/10' : 'bg-[#F5A623]/10 border-[#F5A623]/30'}`}
          data-testid="capture-material-mode"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">{photoOnly ? 'Photo only — no cost' : 'Material cost'}</div>
            <div className="text-[11px] text-gray-400">
              {photoOnly ? "Nothing is added to this project's material spend" : "Added to this project's material spend once you send"}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!photoOnly}
            onClick={() => patch(photoOnly ? { category: 'material' } : { category: null, ...CLEARED_FIELDS })}
            className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              photoOnly ? 'border-[#F5A623]/50 text-[#F5A623] hover:bg-[#F5A623]/10' : 'border-white/20 text-gray-300 hover:bg-white/5'
            }`}
            data-testid="capture-photo-only-toggle"
          >
            {photoOnly ? 'Add cost' : 'Photo only'}
          </button>
        </div>
      ) : (
        /* CATEGORY — three chips. Tap toggles: same category again clears. */
        <div>
          <label className="text-xs text-gray-400 mb-2 block">
            Category <span className="text-gray-500">(optional — leave blank for photo-only)</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map(c => {
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => patch({
                    category: active ? null : c.key,
                    // Clearing category also wipes the downstream fields so a
                    // photo-only capture never carries stale cost data.
                    ...(active ? CLEARED_FIELDS : {}),
                  })}
                  data-testid={`capture-category-${c.key}`}
                  className={`py-3 rounded-lg font-semibold text-sm transition-all active:scale-95 ${
                    active
                      ? 'bg-[#F5A623] text-[#1E2530]'
                      : 'bg-[#2A3240] text-gray-300 border border-white/10 hover:border-[#F5A623]/40'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {category && <ExtractionStatus extraction={extraction} />}

      {/* SUPPLIER / VALUE / REFERENCE / TAGS — only when category set */}
      {category && (
        <div className="space-y-4 pt-1" data-testid="capture-cost-fields-revealed">
          <SupplierPicker
            supplierId={value.supplier_id}
            supplierName={value.supplier_name}
            onPick={(sup) => patch({ supplier_id: sup?.id || null, supplier_name: sup?.name || null })}
            extractedName={extraction?.status === 'done' ? extraction.fields?.supplier_name : null}
            matched={fromReceipt('supplier_name')}
          />

          <div>
            <label className="text-xs text-gray-400 mb-1 block">
              Value <span className="text-gray-500">(gross incl. VAT, as printed on the receipt)</span>
              {fromReceipt('value') && <FromReceipt />}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={value.value ?? ''}
                onChange={(e) => patch({ value: e.target.value })}
                className="pl-7 bg-[#2A3240] border-white/20 text-white text-lg h-11"
                data-testid="capture-value-input"
              />
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Leave blank if the counter didn't give a price — enter the delivery note below.
            </p>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-3">
              <label className="text-xs text-gray-400 mb-1 block">
                Delivery note / receipt no.
                {fromReceipt('reference') && <FromReceipt />}
              </label>
              <Input
                type="text"
                value={value.reference || ''}
                onChange={(e) => patch({ reference: e.target.value })}
                placeholder="e.g. DN-45231"
                className="bg-[#2A3240] border-white/20 text-white h-11"
                data-testid="capture-reference-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-400 mb-1 block">
                Receipt date
                {fromReceipt('invoice_date') && <FromReceipt />}
              </label>
              <Input
                type="date"
                value={value.invoice_date || ''}
                onChange={(e) => patch({ invoice_date: e.target.value })}
                className="bg-[#2A3240] border-white/20 text-white h-11"
                data-testid="capture-invoice-date-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 -mt-2 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            Enter the reference from the delivery note. When the invoice
            arrives later, we'll match it automatically.
          </p>

          <TagInput
            tags={value.tags || []}
            onChange={(next) => patch({ tags: next })}
          />
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------------------------
// SupplierPicker — recent-first list + type-ahead + "Add new" at bottom.
// -------------------------------------------------------------------

export function SupplierPicker({ supplierId, supplierName, onPick, extractedName = null, matched = false }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  // AI read a supplier name we don't have in Contacts yet — offer a one-tap add.
  const showExtractedAdd = !!extractedName && !supplierId;
  const addExtracted = async () => {
    setCreating(true);
    try {
      const res = await api.post('/contacts/suppliers/quick', { name: extractedName });
      onPick({ id: res.data.id, name: res.data.name });
      toast.success(res.data.reused ? `Using existing "${res.data.name}"` : `Added "${res.data.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add supplier');
    } finally {
      setCreating(false);
    }
  };

  const doSearch = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await api.get('/contacts/suppliers', {
        params: { search: s || undefined, limit: 20 },
      });
      setList(res.data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Load list when the dropdown opens (fresh recent-first ordering).
  useEffect(() => { if (open) doSearch(search); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced re-search as the user types.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => doSearch(search), 220);
    return () => clearTimeout(t);
  }, [search, open, doSearch]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const addNew = async () => {
    const name = search.trim();
    if (!name) { toast.error('Enter a supplier name'); return; }
    setCreating(true);
    try {
      const res = await api.post('/contacts/suppliers/quick', { name });
      const sup = res.data;
      onPick({ id: sup.id, name: sup.name });
      setOpen(false);
      setSearch('');
      toast.success(sup.reused ? `Reused existing "${sup.name}"` : `Added "${sup.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add supplier');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="text-xs text-gray-400 mb-1 block">Supplier{matched && <FromReceipt />}</label>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between h-11 px-3 rounded-md bg-[#2A3240] border border-white/20 text-white text-sm text-left"
        data-testid="capture-supplier-btn"
      >
        <span className={supplierName ? '' : 'text-gray-400'}>
          {supplierName || 'Choose or add a supplier'}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {showExtractedAdd && !open && (
        <div className="mt-1.5 flex items-center justify-between gap-2 rounded-md px-2.5 py-2 bg-white/5 border border-white/10" data-testid="capture-supplier-extracted">
          <span className="text-[12px] text-gray-300 truncate">
            Receipt says <span className="text-white font-medium">“{extractedName}”</span>
          </span>
          <button
            type="button"
            onClick={addExtracted}
            disabled={creating}
            className="flex-shrink-0 text-[12px] font-semibold text-[#F5A623] hover:underline disabled:opacity-40"
            data-testid="capture-supplier-add-extracted"
          >
            {creating ? 'Adding…' : 'Use this supplier'}
          </button>
        </div>
      )}

      {open && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-md bg-[#2A3240] border border-white/10 shadow-xl z-50 max-h-72 overflow-hidden flex flex-col"
          data-testid="capture-supplier-menu"
        >
          <div className="p-2 border-b border-white/5">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search or type a new name…"
                className="w-full pl-8 pr-2 py-2 rounded bg-[#1E2530] border border-white/10 text-white text-sm focus:outline-none focus:border-[#F5A623]/50"
                data-testid="capture-supplier-search"
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center text-xs text-gray-400 py-4">Loading…</div>
            ) : list.length === 0 ? (
              <div className="text-center text-xs text-gray-400 py-4">
                {search ? 'No matches — tap "Add new" below' : 'No suppliers yet'}
              </div>
            ) : (
              list.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onPick({ id: s.id, name: s.name }); setOpen(false); setSearch(''); }}
                  className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-white hover:bg-white/5"
                  data-testid={`capture-supplier-option-${s.id}`}
                >
                  <span className="truncate">
                    {s.name}
                    {s.company && s.company !== s.name && (
                      <span className="text-gray-400 text-xs ml-1">· {s.company}</span>
                    )}
                  </span>
                  {supplierId === s.id && <Check className="w-4 h-4 text-[#F5A623]" />}
                </button>
              ))
            )}
          </div>

          {/* Add-new pinned at bottom so it's reachable one-handed. */}
          <button
            type="button"
            onClick={addNew}
            disabled={creating || !search.trim()}
            className="border-t border-white/10 py-2.5 text-sm font-medium text-[#F5A623] hover:bg-[#F5A623]/10 disabled:opacity-40 flex items-center justify-center gap-1.5"
            data-testid="capture-supplier-add-new"
          >
            <Plus className="w-4 h-4" />
            {search.trim() ? `Add "${search.trim()}"` : 'Add new supplier'}
          </button>
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------------------------
// TagInput — chip-style multi-select with autocomplete + free entry.
// -------------------------------------------------------------------

function TagInput({ tags, onChange }) {
  const { api } = useAuth();
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) return;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/cost-ledger/tags/distinct', {
          params: { search: input || undefined, limit: 8 },
        });
        setSuggestions((res.data || []).filter(t => !tags.includes(t)));
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [input, focused, api, tags]);

  const addTag = (t) => {
    const clean = (t || '').trim().toLowerCase();
    if (!clean) return;
    if (tags.includes(clean)) { setInput(''); return; }
    onChange([...tags, clean]);
    setInput('');
  };

  const removeTag = (t) => onChange(tags.filter(x => x !== t));

  return (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">
        Tags <span className="text-gray-500">(optional)</span>
      </label>
      <div
        className="min-h-[44px] flex flex-wrap items-center gap-1.5 rounded-md bg-[#2A3240] border border-white/20 p-2"
        onClick={() => setFocused(true)}
      >
        {tags.map(t => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-[#F5A623]/20 border border-[#F5A623]/40 px-2 py-0.5 text-xs text-[#F5A623] font-medium"
            data-testid={`capture-tags-chip-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(t); }}
              className="hover:opacity-70"
              aria-label={`Remove ${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addTag(input);
            } else if (e.key === 'Backspace' && !input && tags.length) {
              removeTag(tags[tags.length - 1]);
            }
          }}
          placeholder={tags.length ? '' : 'e.g. waste, hire, variation'}
          className="flex-1 min-w-[100px] bg-transparent text-white text-sm outline-none placeholder:text-gray-500"
          data-testid="capture-tags-input"
        />
      </div>
      {focused && suggestions.length > 0 && (
        <div className="mt-1 rounded-md bg-[#2A3240] border border-white/10 max-h-40 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
              className="w-full text-left px-3 py-1.5 text-sm text-white hover:bg-white/5"
              data-testid={`capture-tags-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CostFieldsPanel;
